import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '../../utils/cn'
import { ToastContext, type Toast, type ToastApi, type ToastOptions, type ToastTone } from '../../hooks/useToast'


const TONES: Record<ToastTone, { box: string; icon: string }> = {
  neutral: { box: 'bg-inverse-surface text-inverse-on-surface', icon: 'info' },
  success: { box: 'bg-tertiary-container text-on-tertiary-container', icon: 'check_circle' },
  error: { box: 'bg-error-container text-on-error-container', icon: 'error' },
}

const DEFAULT_DURATION = 5000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  // Timers live outside state so a re-render never restarts a countdown.
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = nextId.current++
      const duration = options?.duration ?? DEFAULT_DURATION
      setToasts((current) => [...current, { id, message, tone: options?.tone ?? 'neutral', duration }])
      if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration))
      return id
    },
    [dismiss],
  )

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, options) => show(message, { ...options, tone: 'success' }),
      error: (message, options) => show(message, { ...options, tone: 'error' }),
      dismiss,
    }),
    [show, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Bottom-centre on mobile so it clears the thumb zone, bottom-right on
          desktop. The region is always mounted so announcements are not lost. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[200] flex flex-col items-center gap-3 p-margin-mobile md:inset-x-auto md:right-0 md:items-end md:p-gutter"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg px-4 py-3',
              TONES[toast.tone].box,
            )}
          >
            <span className="material-symbols-outlined mt-0.5 text-[20px]">{TONES[toast.tone].icon}</span>
            <p className="flex-1 font-body-md text-body-md">{toast.message}</p>
            {/* Not <IconButton>: its hover colours assume a surface background,
                and a toast sits on an inverse or container fill. */}
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
              className="-mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
